import { VITE_API_URL, VITE_APP_BASENAME } from '@/config';
import { t } from '@/i18n';
import { PAGE_LAYOUT_MIN_WIDTH, PUBLIC_SITE_FONT_FAMILY } from '@/utils';
import dayjs from 'dayjs';
import styles from '../assets/scss/styles.module.scss';
import { getBrowserInfo } from './getBrowserInfo';

// https://linear.app/wise-vine/issue/RS-4576/feat-見える化サイト-公開バナー表示
export const PUBLIC_SITE_INFORMATION_END_DATE = dayjs('2026-09-30 23:59');

export const PS_DEFAULT_SEARCH_START_YEAR = 2021;

// frontend/src/features/public-site/files/ にあるファイルが、下記 GitHub Actions で S3 にアップロードされる
// https://github.com/weldrow-com/bns/blob/dev/.github/workflows/deploy-fe-ps.yml
export const FILES_BASE_URL = `${VITE_API_URL}${VITE_APP_BASENAME}/files`;

export const VIEW_PORT_WIDTH_FOR_SP = 1280;

export const BREAK_POINTS = {
  spMax: 768,
  pcMin: PAGE_LAYOUT_MIN_WIDTH,
  pcSm: 1020,
  pcMd: 1280,
} as const;

export const THEME = {
  fonts: {
    fontFamily: PUBLIC_SITE_FONT_FAMILY,
  },
  colors: {
    textPrimary: styles.colorPrimary,
    textSecondary: styles.colorSecondary,
    textTertiary: '#00000099',
    fill: styles.colorPrimary,
    fillSecondary: '#1E417B',
    bgPrimary: styles.colorBgPrimary,
    link: '#E0ECFF',
  },
  zIndexies: {
    header: 1000,
    footer: 3,
  },
} as const;

export const SITE_NAME = t('ps-site-name');

export const SITE_URL = 'https://rssystem.go.jp' as const;

export const PAGE_INFO = {
  top: {
    title: undefined,
    path: '/top',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath() {
      return this.path;
    },
  },
  project: {
    title: t('ps-page-info-projects-title'),
    path: '/project',
    shouldForcePcViewport: true,
    footerType: 'sub',
    createPath() {
      return this.path;
    },
  },
  projectDetail: {
    title: t('ps-page-info-project-title'),
    path: '/project/:id',
    shouldForcePcViewport: true,
    footerType: 'sub',
    createPath(pathname: string) {
      const [, , id] = pathname.split('/');
      return id ? this.path.replace(':id', id) : undefined;
    },
  },
  projectReport: {
    title: t('ps-page-info-project-report-title'),
    path: '/project/:id/report',
    absolutePath: '/report',
    shouldForcePcViewport: true,
    footerType: 'sub',
    createPath(pathname: string) {
      const [, , id] = pathname.split('/');
      return id ? this.path.replace(':id', id) : undefined;
    },
  },
  projectDiff: {
    title: t('ps-page-info-project-diff-title'),
    path: '/project/:id/diff',
    absolutePath: '/diff',
    shouldForcePcViewport: true,
    footerType: 'sub',
    createPath(pathname: string) {
      const [, , id] = pathname.split('/');
      return id ? this.path.replace(':id', id) : undefined;
    },
  },
  analysis: {
    title: t('ps-page-info-analysis-title'),
    path: '/analysis/review-sheet',
    shouldForcePcViewport: true,
    footerType: 'sub',
    createPath() {
      return this.path;
    },
  },
  analysisFundSheet: {
    title: t('ps-page-info-analysis-title'),
    path: '/analysis/fund-sheet',
    shouldForcePcViewport: true,
    footerType: 'sub',
    createPath() {
      return this.path;
    },
  },
  sheets: {
    title: t('ps-page-info-sheets-title'),
    path: '/sheets',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath() {
      return this.path;
    },
  },
  sheetsMinistry: {
    title: t('ps-page-info-sheets-title'),
    path: '/sheets/:year',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath(pathname: string) {
      const [, , year] = pathname.split('/');
      return year ? this.path.replace(':year', year) : undefined;
    },
  },
  sheetsList: {
    title: t('ps-page-info-sheets-title'),
    path: '/sheets/:year/:ministry',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath(pathname: string) {
      const [, , year, ministry] = pathname.split('/');
      return year && ministry
        ? this.path.replace(':year', year).replace(':ministry', ministry)
        : undefined;
    },
  },
  downloadCsv: {
    title: t('ps-page-info-download-csv-title'),
    path: '/download-csv',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath() {
      return this.path;
    },
  },
  downloadCsvList: {
    title: t('ps-page-info-download-csv-title'),
    path: '/download-csv/:year',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath(year: string) {
      return this.path.replace(':year', year);
    },
  },
  notice: {
    title: t('ps-page-info-notice-title'),
    path: '/notice',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath() {
      return this.path;
    },
  },
  howTo: {
    title: t('ps-page-info-how-to-title'),
    path: `${FILES_BASE_URL}/user-guide.pdf`,
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath() {
      return;
    },
  },
  contact: {
    title: t('ps-page-info-contact-title'),
    path: '/contact',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath() {
      return this.path;
    },
  },
  terms: {
    title: t('ps-page-info-terms-title'),
    path: '/terms',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath() {
      return this.path;
    },
  },
  privacyPolicy: {
    title: t('ps-page-info-privacy-policy-title'),
    path: '/privacy-policy',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath() {
      return this.path;
    },
  },
  notFound: {
    title: t('ps-page-info-404-title'),
    path: '*',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath() {
      return '/404';
    },
  },
} as const;

export const MENU_ITEMS = [
  {
    key: 'top',
    label: t('ps-menu-top'),
  },
  {
    key: 'project',
    label: t('ps-menu-project'),
  },
  {
    key: 'analysis',
    label: t('ps-menu-analysis'),
  },
  {
    key: 'howTo',
    label: t('ps-menu-how-to'),
  },
  {
    key: 'notice',
    label: t('ps-menu-notice'),
  },
  {
    key: 'sheets',
    label: t('ps-menu-sheets'),
  },
  {
    key: 'downloadCsv',
    label: t('ps-menu-download-csv'),
  },
  {
    key: 'contact',
    label: t('ps-menu-contact'),
  },
] as const;

export const BROWSER_INFO = getBrowserInfo();
